'use client';

import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import {
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '@/shared/utils/cn';
import {
  searchFieldClearButtonClass,
  searchFieldInputClass,
  searchFieldSubmitButtonClass,
} from './search-field-styles';
import type { SearchComboboxConfig } from './types';

interface SearchInputProps {
  className?: string;
  id: string;
  search: SearchComboboxConfig;
  /** Optional ref to the inner input. Useful para enfocar desde el host (ej. al abrir un sheet). */
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Autofocus al montar. Solo recomendado en sheets/drawers controlados. */
  autoFocus?: boolean;
  /** Keydown handler delegado al input (combobox keyboard nav). */
  onInputKeyDown?: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
  /** id del popup controlado por el input (aria-controls). */
  ariaControls?: string;
  /** aria-expanded del combobox cuando hay popup visible. */
  ariaExpanded?: boolean;
  /** id del item activo en el listbox (aria-activedescendant). */
  ariaActiveDescendant?: string;
}

export const SearchInput = ({
  className,
  id,
  search,
  inputRef: externalInputRef,
  autoFocus,
  onInputKeyDown,
  ariaControls,
  ariaExpanded,
  ariaActiveDescendant,
}: SearchInputProps) => {
  const [localSearch, setLocalSearch] = useState(search.query);
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;
  const query = localSearch.trim();

  useEffect(() => {
    setLocalSearch(search.query);
  }, [search.query]);

  const handleClearSearch = () => {
    setLocalSearch('');
    search.onQueryChange('');
    search.onClear?.();
    inputRef.current?.focus();
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    const nextSearch = localSearch.trim();
    setLocalSearch(nextSearch);
    search.onQueryChange(nextSearch);
    search.onSubmit(event, nextSearch);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setLocalSearch(nextValue);
    search.onQueryChange(nextValue);
  };

  return (
    <div className={cn('relative w-full', className)}>
      <form role="search" onSubmit={handleFormSubmit}>
        <label className="sr-only" htmlFor={id}>
          {search.label ?? 'Buscar'}
        </label>

        <input
          aria-activedescendant={ariaActiveDescendant}
          aria-autocomplete={ariaControls ? 'list' : undefined}
          aria-controls={ariaControls}
          aria-expanded={ariaControls ? (ariaExpanded ?? false) : undefined}
          autoComplete="off"
          autoFocus={autoFocus}
          className={searchFieldInputClass({ hasQuery: query.length > 0 })}
          id={id}
          placeholder={search.placeholder}
          ref={inputRef}
          role={ariaControls ? 'combobox' : undefined}
          type="search"
          value={localSearch}
          onChange={handleInputChange}
          onKeyDown={onInputKeyDown}
        />

        {query.length > 0 ? (
          <button
            aria-label={search.clearAriaLabel ?? 'Limpiar búsqueda'}
            className={searchFieldClearButtonClass()}
            type="button"
            onClick={handleClearSearch}
          >
            <XIcon
              aria-hidden="true"
              className="duration-adipa-fast transition-transform ease-out group-active:rotate-6 group-active:scale-90"
              size={15}
              weight="regular"
            />
          </button>
        ) : null}

        <button
          aria-label={search.submitAriaLabel ?? 'Buscar'}
          className={searchFieldSubmitButtonClass()}
          type="submit"
        >
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="duration-adipa-fast transition-transform ease-out group-active:scale-90"
            size={15}
            weight="bold"
          />
        </button>
      </form>
    </div>
  );
};

export default SearchInput;
